import {
  Users,
  Building2,
  Globe2,
  TrendingUp,
  Briefcase,
  Award,
  Database,
  Layers,
  Zap,
} from "lucide-react";

export const COVERAGE_METRICS = [
  {
    icon: Users,
    value: "100M+",
    label: "Decision-makers in 180 countries",
  },
  {
    icon: Building2,
    value: "19M",
    label: "Companies in 180 countries",
  },
  {
    icon: Globe2,
    value: "4.6M",
    label: "Companies in the U.K.",
  },
  {
    icon: TrendingUp,
    value: "3.6M",
    label: "Marketing execs",
  },
  {
    icon: Briefcase,
    value: "6.8M",
    label: "Sales execs",
  },
  {
    icon: Award,
    value: "2.1M",
    label: "C-level execs",
  },
  {
    icon: Database,
    value: "15",
    label: "Contact data points",
  },
  {
    icon: Layers,
    value: "750",
    label: "Industries covered",
  },
  {
    icon: Zap,
    value: "18",
    label: "Categories of sales triggers",
  },
];

export const KNOWLEDGE_HUB_ARTICLES = [
  {
    id: "multiple-database-sources",
    date: "September 25, 2024",
    tag: "B2B Data Sources Industry",
    title: "Here are 4 Major Benefits of Multiple B2B Database Sources",
    readTime: "4 min read",
    excerpt:
      "In the realm of B2B marketing, establishing a broad network of prospects is just the beginning. Discover the 4 key advantages of multi-source databases...",
    content: [
      {
        type: "lead",
        text: "In the realm of B2B marketing, establishing a broad network of prospects is just the beginning. The true challenge lies in effectively engaging those prospects, guiding them through the sales funnel, and achieving conversions. Thanks to the availability of supplier intent data and diverse B2B data sources, marketing and sales teams now possess unparalleled insights into the buyer journey, allowing them to identify emerging behaviors and trends swiftly.",
      },
      {
        type: "paragraph",
        text: "However, it’s important to recognize that not all B2B data sources are created equal. Many providers rely solely on origin intent data, capturing information only from visitors who interact with their own website content, view advertisements, or engage in content downloads. Others may source data from external B2B database providers but limit their analysis to their internal data. This approach presents a fragmented view—akin to seeing just a single pixel in a larger image.",
      },
      {
        type: "subheading",
        text: "When selecting B2B data providers for your business, consider the following four key advantages of utilizing databases that integrate multiple quality sources:",
      },
      {
        type: "point",
        number: "1",
        title: "Enhanced Overall Data Quality",
        text: "To ensure your B2B sales lead database provides a comprehensive view, it’s essential to monitor prospects’ online behaviors. This step is critical in increasing engagement, which Chief Marketer identified as a primary challenge in generating quality leads in their B2B Lead Gen Outlook. While 51% of respondents pointed to conversion as their biggest hurdle, 59% emphasized the need for 'attractive and specific prospects.' Partnering with a multi-source provider draws from dozens or hundreds of high-quality pipelines, eliminating flawed single-source assumptions.",
      },
      {
        type: "point",
        number: "2",
        title: "Boost Engagement with a Data-Driven Content Strategy",
        text: "Content marketing is now a cornerstone of B2B strategies. According to Content Marketing Institute data, 91% of B2B marketers integrate content marketing regularly. Understanding what your audience is actively searching for allows you to deliver timely, trending topics right into their buyer journey rather than guessing blindly.",
      },
      {
        type: "point",
        number: "3",
        title: "Accelerate Mid-Funnel Conversion from MQL to SQL",
        text: "High-quality intent data is particularly effective in converting Marketing Qualified Leads (MQLs) into Sales Qualified Leads (SQLs). Prospects interacting with your content may still be evaluating competitor offerings or downloading resources without immediate intent. Correlating multi-source intent data with known CRM profiles pinpoints exactly where prospects stand in the sales cycle.",
      },
      {
        type: "point",
        number: "4",
        title: "Justify Your Business Strategy with Concrete Data",
        text: "Conversion costs are a primary concern for modern revenue teams. High-quality B2B data sources aggregate information from numerous repositories and integrate seamlessly with your CRM and Marketing Automation Platform (MAP). The best providers simplify complex telemetry into actionable, revenue-producing business triggers.",
      },
    ],
  },
  {
    id: "direct-mail-difficulties",
    date: "October 20, 2021",
    tag: "Boosting Marketing ROI",
    title: "3 Difficulties with Direct Mail Marketing (and how to overcome them)",
    readTime: "3 min read",
    excerpt:
      "When it comes to direct mail marketing, three of the most common issues clients face are longevity, engagement, and tracking. Here is how digital integration solves them...",
    content: [
      {
        type: "lead",
        text: "When it comes to direct mail marketing, clients often face three common challenges: concerns about longevity, engagement, and campaign tracking. Fortunately, integrating digital elements into your campaign can address each of these issues effectively.",
      },
      {
        type: "subheading",
        text: "The Three Challenges of Direct Mail Marketing (And How to Solve Them)",
      },
      {
        type: "point",
        number: "1",
        title: "Longevity",
        text: "According to the Small Business Association of America, direct mail stays in a household for an average of 17 days—over two weeks! You can extend this shelf life even further by providing added value (such as coupons, key industry dates, or actionable tips) and pairing mailers with retargeted landing pages so recipients have a dedicated digital destination.",
      },
      {
        type: "point",
        number: "2",
        title: "Engagement",
        text: "Print-only campaigns can struggle with engagement without immediate digital bridges. Leverage USPS Informed Delivery daily previews with interactive links, integrate high-contrast QR codes directly on cards, and fire coordinated social/display retargeting ads to every recipient address.",
      },
      {
        type: "point",
        number: "3",
        title: "Tracking",
        text: "Direct mail is fully measurable with the right infrastructure. Use USPS Smart Mail Barcodes for delivery confirmation, unique campaign-specific phone numbers for inbound call attribution, and dedicated UTM landing page URLs to trace form completions back to specific mail drops.",
      },
      {
        type: "conclusion",
        text: "Direct mail is a powerful, trusted marketing channel. When reinforced by digital tracking and multi-touch workflows, it delivers the tangible authority of print with the real-time measurement of digital marketing.",
      },
    ],
  },
  {
    id: "enhance-roi-campaign",
    date: "September 19, 2024",
    tag: "Boosting Marketing ROI",
    title: "How to Boost Marketing ROI: Strategies for Maximizing Your Efforts",
    readTime: "4 min read",
    excerpt:
      "According to an Adobe survey of 1,000 U.S. marketers, 68% feel the most pressure to deliver a return on marketing investments. Here are 3 actionable strategies to scale your ROI...",
    content: [
      {
        type: "lead",
        text: "According to a recent Adobe survey of 1,000 U.S. marketers, 68% feel the most pressure to deliver a return on marketing investments (ROI). As marketing increasingly takes over the presale process, teams face heightened expectations to deliver measurable pipeline contributions.",
      },
      {
        type: "paragraph",
        text: "A key challenge in proving marketing ROI is the variety of ways to measure it. The ultimate goal is to get 'more' out of our campaigns—whether that means higher lead velocity, deeper content engagement, or personalized customer journeys. Here are three actionable strategies to systematically increase your return on investment:",
      },
      {
        type: "point",
        number: "1",
        title: "Improve Lead Capture with Interactive Content",
        text: "Delivering qualified leads is the baseline of any marketing campaign. B2B buyers are increasingly hesitant to fill out friction-heavy forms for static PDFs; DemandGen reports that only 11% of buyers are willing to share information for a generic whitepaper. By contrast, interactive content (calculators, assessments, and diagnostics) provides upfront value and converts at twice the rate of static assets.",
      },
      {
        type: "point",
        number: "2",
        title: "Collect More and Better First-Party Data",
        text: "Rather than relying on rough inferences to score leads, capture explicit intent through interactive tools. For example, Blackbaud introduced interactive product calculators that enabled prospects to model their own business returns; this enriched sales handoffs with concrete customer variables, helping their team exceed quota by 133% and generate $600,000 in new revenue.",
      },
      {
        type: "point",
        number: "3",
        title: "Increase Structured Email Touchpoints via Content Pillars",
        text: "Email marketing remains one of the highest-yield channels in B2B. Rather than blasting the same promotional copy repeatedly, adopt a content pillar approach: break a primary cornerstone asset into bite-sized summaries, data infographics, webinar invites, and executive takeaways to sustain meaningful engagement without prospect fatigue.",
      },
    ],
  },
];